import type { AuthProviderConfig } from "../../auth/authProviders";
import { MicrosoftGlyph, ProviderIcon } from "./icons";

/**
 * The non-Atlas sign-in methods, as a row of tiles BELOW the login card —
 * the same treatment Atlas Desktop gives Help Center / Claim Status / Auth
 * Forms. Being outside the card is the point: it reads as "other ways in",
 * not as a second primary.
 *
 * Layout rule: two tiles sit side by side stacked (icon over label); a lone
 * tile lays out as a single-line row instead. Stretching the stacked shape to
 * full width leaves a tall box with a 20px glyph adrift in it, and the extra
 * height reads as more emphasis than a secondary option should carry.
 */
export interface SecondaryTilesProps {
  providers: AuthProviderConfig[];
  onPick: (provider: AuthProviderConfig) => void;
}

/** Short tile captions — "Pathways account" is too long for a tile. */
const TILE_LABEL: Record<string, string> = {
  entra: "Astrana SSO",
  "pathways-b2c": "Pathways",
};

export default function SecondaryTiles({
  providers,
  onPick,
}: SecondaryTilesProps) {
  if (providers.length === 0) return null;

  const solo = providers.length === 1;
  const base =
    "rounded-xl border border-border/60 bg-muted/60 dark:bg-card/60 px-4 text-foreground hover:border-primary hover:text-primary hover:bg-muted transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background flex items-center justify-center";
  const shape = solo
    ? " flex-row gap-2.5 py-3.5"
    : " flex-col gap-1.5 py-4";

  return (
    <div
      className={`mt-5 w-full max-w-sm grid gap-3 ${
        solo ? "grid-cols-1" : "grid-cols-2"
      }`}
    >
      {providers.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onPick(p)}
          className={base + shape}
        >
          {p.id === "entra" ? (
            <MicrosoftGlyph />
          ) : (
            <ProviderIcon id={p.id} />
          )}
          <span className="text-xs font-semibold">
            {TILE_LABEL[p.id] ?? p.label}
          </span>
        </button>
      ))}
    </div>
  );
}
